import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { ProfilecardTriggerPosition } from '@atlaskit/profilecard/types';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics';

import { ReportingLinesProvider } from './ReportingLinesContext';
import { ReportingLinesWithQuery } from './ReportingLinesWithQuery';

type ReportingLinesProps = {
	product?: string;
	userId: string;
	WrappedWithProfilecard?: React.ComponentType<{
		children: React.ReactElement;
		userId: string;
		position?: ProfilecardTriggerPosition;
	}>;
	/**
	 * Optional attributes to merge into the ReportingLinesChart.viewed event (e.g. tab, isNewUserProfile for user profile)
	 */
	viewedEventAttributes?: Record<string, string | boolean>;
	selectedCardRef?: React.RefObject<HTMLDivElement>;
	/**
	 * Cloud id used to build the site ARI required by the KnowledgeDiscovery
	 * `reportingLines` query (used when the
	 * `ptc-reporting-lines-knowledge-discovery-query` gate is enabled). Prefer
	 * passing this explicitly from the host — it is required for consumers
	 * rendered outside of `TeamsWrapper` (e.g. search). Falls back to the Teams
	 * app-config store when not provided.
	 */
	cloudId?: string;
	/**
	 * Org id required by the KnowledgeDiscovery `reportingLines` query (used when
	 * the gate is enabled). Prefer passing this explicitly from the host. Falls
	 * back to the Teams app-config store when not provided.
	 */
	orgId?: string;
};

/**
 * The Reporting Lines component is a resourced component that fetches the reporting lines for a given user and renders them in a chart.
 * @param product - The host product of the reporting lines, use for analytics.
 * @param userId - The ID of the user to get the reporting lines for the starting point of the chart.
 * @param WrappedWithProfilecard - A resourced component to wrap reporting lines in profile cards with actions relevant to the context.
 * @param viewedEventAttributes - Optional attributes to merge into the ReportingLinesChart.viewed event.
 * @param selectedCardRef - A ref to the selected card in the profile card.
 * @param cloudId - The cloud id used to build the site ARI required by the KnowledgeDiscovery `reportingLines` query, when outside of TeamsWrapper context.
 * @param orgId - The org id required by the KnowledgeDiscovery `reportingLines` query, when outside of TeamsWrapper context.
 */
export const ReportingLines = ({
	product,
	userId,
	WrappedWithProfilecard,
	viewedEventAttributes,
	selectedCardRef,
	cloudId,
	orgId,
}: ReportingLinesProps): React.JSX.Element => {
	if (fg('ptc-enable-ptc-common-analytics-refactor')) {
		return (
			<TeamsAppAnalyticsContext data={{ source: 'userProfileScreen' }}>
				<ReportingLinesProvider
					product={product}
					WrappedWithProfilecard={WrappedWithProfilecard}
					viewedEventAttributes={viewedEventAttributes}
					selectedCardRef={selectedCardRef}
				>
					<ReportingLinesWithQuery
						userId={userId}
						product={product}
						cloudId={cloudId}
						orgId={orgId}
					/>
				</ReportingLinesProvider>
			</TeamsAppAnalyticsContext>
		);
	}

	return (
		<ReportingLinesProvider
			product={product}
			WrappedWithProfilecard={WrappedWithProfilecard}
			viewedEventAttributes={viewedEventAttributes}
			selectedCardRef={selectedCardRef}
		>
			<ReportingLinesWithQuery userId={userId} product={product} cloudId={cloudId} orgId={orgId} />
		</ReportingLinesProvider>
	);
};
